import React, { useState } from 'react';
import BasicModal from '../../newStudent/Modal/BasicModal';
import { IChatUser } from '../../../interfaces/chat/chat.interface';
import { HiChatAlt2, HiSearch, HiCheck } from 'react-icons/hi';
import chatService from '../../../services/chat/chat.service';
import { useLoading } from '../../agent/Context/LoadingContext';
import { warningToast, errorToast } from '../../common/Toast';
import * as S from './styles';
import { ButtonSimple } from '../../newStudent/Buttons';
import HeaderModal from '../../agent/HeaderModal';

interface IBulkMessageProps {
  isOpen: boolean;
  onClose: () => void;
  users: IChatUser[];
  onSuccess?: () => void;
}

export default function BulkMessage({ isOpen, onClose, users, onSuccess }: IBulkMessageProps) {
  const { setIsLoading } = useLoading();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleToggleUser = (userId: number) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const handleSend = async () => {
    if (selectedUsers.length === 0 || !message.trim()) {
      warningToast('Selecione pelo menos um destinatário e digite uma mensagem');
      return;
    }

    setSending(true);
    setIsLoading(true);
    try {
      const success = await chatService.sendBulkMessage(selectedUsers, message);
      if (success) {
        setSelectedUsers([]);
        setMessage('');
        setSearchTerm('');
        onSuccess?.();
        onClose();
      } else {
        errorToast('Erro ao enviar mensagem em lote');
      }
    } catch (error) {
      errorToast('Erro ao enviar mensagem em lote');
    } finally {
      setSending(false);
      setIsLoading(false);
    }
  };

  return (
    <BasicModal isOpen={isOpen} setIsOpen={() => onClose()} handleOnClose={onClose} isFloat>
      <S.Container>
        <HeaderModal
          setOpen={onClose}
          title="Mensagem em Lote"
          subtitle
          icon={<HiChatAlt2 size={24} />}
          handleClose={() => onClose()}
        />
        <S.Content>
          <S.Description>
            Selecione os destinatários e digite a mensagem que será enviada para todos:
          </S.Description>

          <S.SearchWrapper>
            <S.SearchIcon>
              <HiSearch />
            </S.SearchIcon>
            <S.SearchInput
              type="text"
              placeholder="Buscar contato..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </S.SearchWrapper>

          <S.UserList>
            {filteredUsers.length === 0 ? (
              <S.EmptyState>
                <S.EmptyText>Nenhum contato encontrado</S.EmptyText>
              </S.EmptyState>
            ) : (
              filteredUsers.map((user) => {
                const isSelected = selectedUsers.includes(user.id);
                return (
                  <S.UserItem
                    key={user.id}
                    onClick={() => handleToggleUser(user.id)}
                    $selected={isSelected}
                  >
                    <S.Checkbox $selected={isSelected}>
                      {isSelected && <HiCheck />}
                    </S.Checkbox>
                    <S.AvatarContainer>
                      {user.avatar ? (
                        <S.Avatar src={user.avatar} alt={user.name} />
                      ) : (
                        <S.AvatarPlaceholder>
                          {user.name.charAt(0).toUpperCase()}
                        </S.AvatarPlaceholder>
                      )}
                      {user.isOnline === true ? (
                        <S.OnlineBadge />
                      ) : user.isOnline === false ? (
                        <S.OfflineBadge />
                      ) : null}
                    </S.AvatarContainer>
                    <S.UserInfo>
                      <S.UserName>{user.name}</S.UserName>
                      <S.UserStatus>{user.isOnline ? 'Online' : 'Offline'}</S.UserStatus>
                    </S.UserInfo>
                  </S.UserItem>
                );
              })
            )}
          </S.UserList>

          <S.MessageWrapper>
            <S.MessageLabel>Mensagem:</S.MessageLabel>
            <S.MessageTextarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Digite sua mensagem aqui..."
              rows={4}
            />
          </S.MessageWrapper>

          <S.Footer>
            <S.SelectedCount>
              {selectedUsers.length} destinatário{selectedUsers.length !== 1 ? 's' : ''} selecionado
              {selectedUsers.length !== 1 ? 's' : ''}
            </S.SelectedCount>
            <S.ButtonGroup>
              <ButtonSimple color="gray" font="p" widthSize="medium" onClick={onClose} disabled={sending}>
                Cancelar
              </ButtonSimple>
              <ButtonSimple color="primary" font="p" widthSize="medium" onClick={handleSend} disabled={sending || selectedUsers.length === 0}>
                {sending ? 'Enviando...' : 'Enviar'}
              </ButtonSimple>
            </S.ButtonGroup>
          </S.Footer>
        </S.Content>
      </S.Container>
    </BasicModal>
  );
}

